import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, FilePlus2, Info, PackagePlus, Pencil, Sparkles } from 'lucide-react';
import { api } from '../../lib/api';
import type { CollectionTypeDef, CollectionItem } from '../../lib/types';
import { pickLoc, itemTitle } from '../../lib/collections';
import Modal from '../../components/ui/Modal';
import CollectionForm from '../../components/forms/CollectionForm';
import ContextCard from '../../components/ui/ContextCard';
import PageContainer from '../../components/ui/PageContainer';
import PublishBanner from '../../components/ui/PublishBanner';

function collectionContext(type: CollectionTypeDef, singular: string, plural: string) {
  const name = plural.toLocaleLowerCase('es');
  const item = singular.toLocaleLowerCase('es');
  const detailPage = type.hasDetailPage
    ? `Cada ${item} puede tener su propia página con información completa.`
    : `Estos datos pueden mostrarse en distintas partes del sitio sin crear una página independiente para cada ${item}.`;

  switch (type.archetype) {
    case 'listing':
      return {
        title: `¿Qué son ${name}?`,
        description: `Son fichas individuales que tus visitantes pueden explorar y comparar, por ejemplo por ubicación, características o categoría.`,
        detail: `${detailPage} Publica solo los elementos que ya estén listos para mostrarse.`,
      };
    case 'catalog':
      return {
        title: `¿Qué son ${name}?`,
        description: `Son las opciones que ofrece tu negocio, como productos, servicios o elementos de un menú. Aquí defines qué incluye cada ${item}, su precio y sus imágenes cuando correspondan.`,
        detail: `${detailPage} Esta sección presenta tu oferta; no controla por sí sola las reservas ni los pagos.`,
      };
    case 'people':
      return {
        title: `¿Qué son ${name}?`,
        description: `Son perfiles de personas relacionadas con tu negocio, como integrantes del equipo, agentes o especialistas.`,
        detail: `${detailPage} Agrega solo la información profesional que quieras hacer pública.`,
      };
    case 'social':
      return {
        title: `¿Qué son ${name}?`,
        description: `Son contenidos que generan confianza, como opiniones, experiencias o casos compartidos por clientes.`,
        detail: `Revisa nombres, textos y permisos antes de publicarlos. ${detailPage}`,
      };
    case 'editorial':
      return {
        title: `¿Qué son ${name}?`,
        description: `Son publicaciones informativas para compartir novedades, consejos o conocimiento de tu negocio.`,
        detail: `${detailPage} Puedes preparar un artículo como borrador y publicarlo cuando esté terminado.`,
      };
    case 'ops':
      return {
        title: `¿Qué son ${name}?`,
        description: `Son datos prácticos que ayudan a explicar cómo funciona tu negocio, como sucursales, preguntas frecuentes o información operativa.`,
        detail: `${detailPage} Mantén esta información actualizada para evitar dudas o datos contradictorios en el sitio.`,
      };
  }
}

export default function CollectionListPage() {
  const { key = '' } = useParams();
  const [type, setType] = useState<CollectionTypeDef | null>(null);
  const [items, setItems] = useState<CollectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [modal, setModal] = useState<'create' | CollectionItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CollectionItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [installing, setInstalling] = useState(false);
  // Generación de un ítem completo con IA: prompt → /api/ai/item → precarga el form.
  const [genOpen, setGenOpen] = useState(false);
  const [genPrompt, setGenPrompt] = useState('');
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState('');
  const [prefill, setPrefill] = useState<Record<string, unknown> | null>(null);
  const [needsPublish, setNeedsPublish] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');
    setActionError('');
    setSuccess('');
    setNotFound(false);
    setModal(null);
    setDeleteTarget(null);
    setNeedsPublish(false);
    Promise.all([api.collectionTypes.get(key), api.collections.list(key)])
      .then(([t, list]) => { setType(t); setItems(list); })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : '';
        if (msg.toLowerCase().includes('not found') || msg.includes('404')) {
          setNotFound(true);
        } else {
          setError('No se pudo cargar esta sección. Intenta recargar la página.');
        }
      })
      .finally(() => setLoading(false));
  }, [key]);

  const singular = type ? pickLoc(type.labels.singular, 'es') || type.key : '';
  const plural = type ? pickLoc(type.labels.plural, 'es') || type.key : '';
  const publishedCount = items.filter((item) => item.status === 'published').length;

  useEffect(() => {
    if (!success) return;
    const timeout = window.setTimeout(() => setSuccess(''), 4000);
    return () => window.clearTimeout(timeout);
  }, [success]);

  function handleSave(saved: CollectionItem) {
    const wasExisting = items.some((item) => item.id === saved.id);
    setItems((prev) =>
      prev.some((i) => i.id === saved.id)
        ? prev.map((i) => (i.id === saved.id ? saved : i))
        : [saved, ...prev],
    );
    setModal(null);
    setPrefill(null);
    setActionError('');
    setNeedsPublish(true);
    setSuccess(wasExisting
      ? `${singular || 'El contenido'} se actualizó correctamente.`
      : `${singular || 'El contenido'} se creó correctamente.`);
  }

  async function handleGenerate() {
    setGenLoading(true);
    setGenError('');
    try {
      const { data } = await api.ai.item(key, genPrompt.trim() || undefined);
      setPrefill(data);
      setGenOpen(false);
      setGenPrompt('');
      setModal('create'); // abre el form precargado para revisar antes de guardar
    } catch (e) {
      setGenError('No se pudo generar el contenido. Intenta de nuevo o créalo manualmente.');
    } finally {
      setGenLoading(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError('');
    try {
      await api.collections.delete(key, deleteTarget.id);
      setItems((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      setDeleteTarget(null);
      setNeedsPublish(true);
      setSuccess(`${singular || 'El contenido'} se eliminó correctamente.`);
    } catch {
      setActionError('No se pudo eliminar. Intenta de nuevo.');
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-ui-border bg-ui-surface px-5 py-8 text-center">
        <p className="text-sm text-ui-text-muted">Cargando contenido…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center space-y-5">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-ui-surface border border-ui-border">
          <PackagePlus size={26} className="text-ui-text-muted" />
        </div>
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-ui-text">Esta sección aún no está activada</h2>
          <p className="text-sm text-ui-text-muted">
            Actívala para empezar a agregar contenido y prepararlo antes de mostrarlo en tu sitio.
          </p>
        </div>
        <motion.button
          onClick={async () => {
            setInstalling(true);
            try {
              await api.onboarding.instantiate([key]);
              const [t, list] = await Promise.all([api.collectionTypes.get(key), api.collections.list(key)]);
              setType(t); setItems(list); setNotFound(false);
            } catch {
              setError('No se pudo activar la sección. Intenta de nuevo.');
            } finally {
              setInstalling(false);
            }
          }}
          disabled={installing}
          whileTap={{ scale: 0.97 }}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-ui-accent text-ui-bg text-sm font-medium rounded-lg disabled:opacity-50"
        >
          <PackagePlus size={16} />
          {installing ? 'Activando…' : 'Activar sección'}
        </motion.button>
        {error && <p className="text-sm text-red-400">{error}</p>}
      </div>
    );
  }

  if (error || !type) {
    return <p className="text-sm text-ui-text-muted">No se pudo cargar esta sección. Intenta recargar la página.</p>;
  }

  return (
    <PageContainer>
      {success && (
        <div className="flex items-start gap-2.5 rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-4 py-3">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-400" />
          <p className="text-sm text-ui-text-muted">{success}</p>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-ui-text">{plural}</h1>
          <Link
            to="/settings?tab=colecciones"
            title="Renombrar sección"
            className="text-ui-text-subtle hover:text-ui-text transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <motion.button
            onClick={() => { setGenError(''); setGenOpen(true); }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-ui-accent/40 text-ui-accent text-sm rounded-md hover:bg-ui-accent/10 transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Generar con IA
          </motion.button>
          <button
            onClick={() => { setPrefill(null); setModal('create'); }}
            className="px-3 py-1.5 bg-ui-accent text-ui-bg text-sm rounded-md hover:bg-ui-accent-hover"
          >
            + Nuevo{singular ? ` — ${singular.toLowerCase()}` : ''}
          </button>
        </div>
      </div>

      <ContextCard {...collectionContext(type, singular, plural)} />

      <PublishBanner needsPublish={needsPublish} onPublished={() => setNeedsPublish(false)} />

      {items.length > 0 && publishedCount === 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-sky-500/25 bg-sky-500/10 px-4 py-3">
          <Info size={16} className="mt-0.5 shrink-0 text-sky-400" />
          <p className="text-sm text-ui-text-muted">
            Todo el contenido está en borrador. No aparecerá en el sitio hasta que publiques al menos un elemento y publiques los cambios del sitio.
          </p>
        </div>
      )}

      <div className="bg-ui-surface rounded-lg border border-ui-border divide-y divide-ui-border">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between px-4 py-3 gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-ui-text truncate">
                {itemTitle(it)}
                {it.featured && <span className="ml-1.5 text-ui-warning">★</span>}
              </p>
              <p className="text-sm text-ui-text-subtle">
                /{it.slug || '—'} · {it.status === 'published' ? 'Publicado' : 'Borrador'}
              </p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <button onClick={() => setModal(it)} className="text-sm text-ui-accent hover:underline">
                Editar
              </button>
              <button onClick={() => setDeleteTarget(it)} className="text-sm text-ui-danger hover:opacity-80">
                Eliminar
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="flex flex-col items-center px-6 py-12 text-center">
            <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-ui-border bg-ui-bg">
              <FilePlus2 size={22} className="text-ui-text-muted" />
            </span>
            <h2 className="text-base font-semibold text-ui-text">Aún no hay {plural.toLowerCase()}</h2>
            <p className="mt-1 max-w-md text-sm text-ui-text-muted">
              Crea tu primer {singular.toLowerCase()} y decide si quieres publicarlo ahora o guardarlo como borrador para terminarlo después.
            </p>
            <button
              type="button"
              onClick={() => { setPrefill(null); setModal('create'); }}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-bg hover:bg-ui-accent-hover"
            >
              <FilePlus2 size={16} />
              Crear {singular.toLowerCase()}
            </button>
          </div>
        )}
      </div>

      {genOpen && (
        <Modal title={`Generar ${singular.toLowerCase()} con IA`} onClose={() => setGenOpen(false)}>
          <div className="space-y-4">
            <p className="text-sm text-ui-text-muted">
              Describe en una línea lo que quieres y la IA creará un {singular.toLowerCase()} completo
              (en español e inglés) para que lo revises antes de guardar.
            </p>
            <textarea
              value={genPrompt}
              onChange={(e) => setGenPrompt(e.target.value)}
              rows={3}
              autoFocus
              placeholder={`Ej. ${singular.toLowerCase()} destacado, tono profesional y cercano`}
              className="w-full bg-ui-bg border border-ui-border rounded-md px-3 py-2 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent resize-none"
            />
            {genError && <p className="text-sm text-ui-danger">{genError}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setGenOpen(false)} className="px-4 py-2 text-sm text-ui-text-muted hover:text-ui-text">
                Cancelar
              </button>
              <motion.button
                type="button"
                onClick={handleGenerate}
                disabled={genLoading}
                whileTap={{ scale: 0.97 }}
                className="flex items-center gap-1.5 px-4 py-2 bg-ui-accent text-ui-bg text-sm font-medium rounded-md disabled:opacity-50 hover:opacity-90 transition-opacity"
              >
                <Sparkles className="w-4 h-4" />
                {genLoading ? 'Generando…' : 'Generar'}
              </motion.button>
            </div>
          </div>
        </Modal>
      )}

      {modal && (
        <CollectionForm
          type={type}
          initial={modal === 'create' ? undefined : (modal as CollectionItem)}
          prefillData={modal === 'create' ? (prefill ?? undefined) : undefined}
          onSave={handleSave}
          onCancel={() => { setModal(null); setPrefill(null); }}
        />
      )}

      {deleteTarget && (
        <Modal title={`Eliminar ${singular.toLowerCase()}`} onClose={() => setDeleteTarget(null)}>
          <div className="space-y-4">
            <p className="text-sm text-ui-text-muted">
              ¿Eliminar permanentemente{' '}
              <span className="text-ui-text font-medium">"{itemTitle(deleteTarget)}"</span>?
            </p>
            <p className="text-sm text-ui-text-subtle">
              Esta acción no se puede deshacer. Si está publicado, dejará de aparecer después de la próxima publicación del sitio.
            </p>
            {actionError && (
              <div className="flex items-start gap-2 rounded-md border border-red-500/25 bg-red-500/10 px-3 py-2">
                <AlertTriangle size={15} className="mt-0.5 shrink-0 text-red-400" />
                <p className="text-sm text-ui-text-muted">{actionError}</p>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm text-ui-text-muted hover:text-ui-text"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 bg-ui-danger hover:bg-ui-danger/85 text-ui-text-inverse text-sm rounded-md disabled:opacity-50"
              >
                {deleting ? 'Eliminando…' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
}
